import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Search, Loader2, TrendingUp } from 'lucide-react'
import { useWatchlistStore } from '../store/watchlistStore'
import { apiFetch } from '../lib/api'
import Button from './ui/Button'

interface AddToWatchlistModalProps {
  isOpen: boolean
  onClose: () => void
}

interface LookupResult {
  symbol: string
  name: string
  price: number
  currency: string
}

const AddToWatchlistModal: React.FC<AddToWatchlistModalProps> = ({ isOpen, onClose }) => {
  const [symbol, setSymbol] = useState('')
  const [type, setType] = useState<'us_stock' | 'th_stock'>('us_stock')
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null)
  const [lookupError, setLookupError] = useState('')
  const [looking, setLooking] = useState(false)
  const [adding, setAdding] = useState(false)
  const { addItem } = useWatchlistStore()

  const handleLookup = async () => {
    if (!symbol.trim()) return
    setLooking(true)
    setLookupResult(null)
    setLookupError('')
    try {
      const res = await apiFetch(`/api/watchlist/lookup?symbol=${encodeURIComponent(symbol.trim())}&type=${type}`)
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.error || 'ไม่พบหุ้นนี้')
      }
      const data = await res.json()
      setLookupResult(data)
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'ไม่พบหุ้นนี้')
    } finally {
      setLooking(false)
    }
  }

  const handleAdd = async () => {
    if (!lookupResult) return
    setAdding(true)
    try {
      await addItem(lookupResult.symbol, lookupResult.name, type)
      handleClose()
    } catch {
      // error already toasted
    } finally {
      setAdding(false)
    }
  }

  const handleClose = () => {
    setSymbol('')
    setType('us_stock')
    setLookupResult(null)
    setLookupError('')
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLookup()
  }

  if (!isOpen) return null

  return createPortal(
    <AnimatePresence>
      <div className="sheet fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 z-10"
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">เพิ่มหุ้นใน Watchlist</h2>
            <button
              onClick={handleClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Stock Type */}
          <div className="flex gap-2 mb-4">
            {(['us_stock', 'th_stock'] as const).map((t) => (
              <button
                key={t}
                onClick={() => { setType(t); setLookupResult(null); setLookupError('') }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  type === t
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {t === 'us_stock' ? '🇺🇸 US Stock' : '🇹🇭 Thai Stock (SET)'}
              </button>
            ))}
          </div>

          {/* Symbol Input */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={symbol}
              onChange={(e) => { setSymbol(e.target.value.toUpperCase()); setLookupResult(null); setLookupError('') }}
              onKeyDown={handleKeyDown}
              placeholder={type === 'us_stock' ? 'เช่น AAPL, TSLA' : 'เช่น PTT, AOT'}
              className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <Button variant="secondary" size="sm"
              onClick={handleLookup}
              disabled={!symbol.trim() || looking}
            >
              {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>

          {/* Lookup Error */}
          {lookupError && (
            <p className="text-sm text-loss mb-3">{lookupError}</p>
          )}

          {/* Lookup Result */}
          {lookupResult && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700"
            >
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-indigo-500" />
                <span className="font-semibold text-indigo-700 dark:text-indigo-300">{lookupResult.symbol}</span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{lookupResult.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                ราคาปัจจุบัน: {lookupResult.currency === 'THB' ? '฿' : '$'}{lookupResult.price.toFixed(2)} {lookupResult.currency}
              </p>
            </motion.div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" className="flex-1" onClick={handleClose}>
              ยกเลิก
            </Button>
            <Button size="sm" className="flex-1"
              onClick={handleAdd}
              disabled={!lookupResult || adding}
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              เพิ่มใน Watchlist
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  )
}

export default AddToWatchlistModal
